import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import RecoveryCodeModal from "../components/RecoveryCodeModal";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function SignupEmployer() {
  const { signupEmployer } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await signupEmployer(form);
      if (data?.recovery_code) {
        setRecoveryCode(data.recovery_code);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          t("common.error_generic")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const finishAfterModal = () => {
    setRecoveryCode(null);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="signup-employer-success">
        <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-12">
          <Link to="/" className="mb-6 flex items-center gap-2 text-slate-900">
            <img src="/Logo.png" alt="Talent Junction" className="h-14 w-14 rounded-xl object-cover" />
            <span className="text-base font-semibold tracking-tight">{t("brand")}</span>
          </Link>
          <div className="w-full rounded-2xl border border-amber-200 bg-white p-8 shadow-sm">
            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4"/><path d="M12 16h.01"/><circle cx="12" cy="12" r="10"/></svg>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              {t("signup_employer_success.title")}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {t("signup_employer_success.body")}
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-6 inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              data-testid="signup-employer-success-cta"
            >
              {t("signup_employer_success.cta")}
            </button>
          </div>
        </main>
        {recoveryCode && <RecoveryCodeModal code={recoveryCode} variant="signup" onClose={finishAfterModal} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="signup-employer-page">
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 py-12">
        <Link to="/" className="mb-6 flex items-center gap-2 text-slate-900" data-testid="signup-brand-link">
          <img src="/Logo.png" alt="Talent Junction" className="h-14 w-14 rounded-xl object-cover" />
          <span className="text-base font-semibold tracking-tight">{t("brand")}</span>
        </Link>
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {t("auth.employer_title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{t("auth.employer_subtitle")}</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="signup-employer-form">
            <Field
              label={t("auth.company_name")}
              value={form.company_name}
              onChange={set("company_name")}
              testId="signup-employer-company-input"
              required
            />
            <Field
              label={t("auth.contact_name")}
              value={form.contact_name}
              onChange={set("contact_name")}
              testId="signup-employer-contact-input"
              required
            />
            <Field
              type="email"
              label={t("auth.email")}
              value={form.email}
              onChange={set("email")}
              testId="signup-employer-email-input"
              required
            />
            <Field
              type="password"
              label={t("auth.password")}
              value={form.password}
              onChange={set("password")}
              testId="signup-employer-password-input"
              required
              minLength={8}
            />

            {error && (
              <div
                className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                data-testid="signup-employer-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="signup-employer-submit-btn"
            >
              {submitting ? t("common.loading") : t("auth.submit_signup_employer")}
            </button>

            <p className="text-xs text-slate-500" data-testid="signup-employer-cgv-note">
              {t("legal.signup_accept")}
              <Link to="/legal/cgv" className="font-medium text-indigo-700 hover:text-indigo-800">
                {t("legal.signup_accept_cgv")}
              </Link>
              .
            </p>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            {t("auth.already_have")}{" "}
            <Link to="/login" className="font-medium text-indigo-700 hover:text-indigo-800">
              {t("auth.login_link")}
            </Link>
          </p>
        </div>
      </main>

      {recoveryCode && (
        <RecoveryCodeModal code={recoveryCode} variant="signup" onClose={finishAfterModal} />
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required, minLength }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input
        type={type}
        required={required}
        minLength={minLength}
        value={value}
        onChange={onChange}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        data-testid={testId}
      />
    </div>
  );
}
